import { Typography } from "@fullscript/aviary-web";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import appStoreQR from "@shared/assets/downloadApp/appStoreQR.png";
import { useMediaQuery } from "@shared/hooks/useMediaQuery/useMediaQuery";
import { l } from "@unauthenticated/shared/locales/i18n";

import { AppRatingRow } from "./AppRatingRow/AppRatingRow";
import mobileAppSlideBg from "./assets/mobileAppSlideBg.webp";

import * as styles from "./MobileAppSlide.styles";

const mobileAppSlideBackdropURL =
  "https://public-assets.fullscript.com/assets/download-mobile-backdrop.webm";

const BACKDROP_VISIBILITY_THRESHOLD = 0.5;

const MobileAppSlide = () => {
  const { t } = useTranslation();
  const backdropRef = useRef<HTMLVideoElement>(null);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    const backdrop = backdropRef.current;

    if (!backdrop) return;

    if (prefersReducedMotion) {
      backdrop.pause();

      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          /* Browsers reject a pending play() when pause() interrupts it, so a rotating
             carousel produces AbortErrors that carry no signal. */
          backdrop.play().catch(() => undefined);
        } else {
          backdrop.pause();
        }
      },
      { threshold: BACKDROP_VISIBILITY_THRESHOLD }
    );

    observer.observe(backdrop);

    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  return (
    <div css={styles.slide}>
      <div css={styles.background} aria-hidden>
        <video
          ref={backdropRef}
          css={styles.backgroundVideo}
          poster={mobileAppSlideBg}
          preload="none"
          muted
          loop
          playsInline
        >
          <source src={mobileAppSlideBackdropURL} type="video/webm" />
        </video>
      </div>
      <div css={styles.content}>
        <div css={styles.qrTile}>
          <img
            src={appStoreQR}
            alt={t(l.sideCarousel.slides.mobileApp.qrCodeAlt)}
            css={styles.qrImage}
            draggable={false}
          />
        </div>
        <div css={styles.textStack}>
          <Typography variant="h3" intention="inverse" isMarginless>
            {t(l.sideCarousel.slides.mobileApp.title)}
          </Typography>
          <Typography variant="footnote" intention="inverse" isMarginless>
            {t(l.sideCarousel.slides.mobileApp.description)}
          </Typography>
          <AppRatingRow />
        </div>
      </div>
    </div>
  );
};

export { MobileAppSlide };
